import axios from '@providers/axiosProvider';
import * as actions from './actions';
import {
  APIGetMonthlyPlansRequestParams,
  APIGetMonthlyPlansResponse,
  APIGetMonthlyTargetPlansRequestParams,
  APIGetMonthlyTargetPlansResponse,
  APIPutEditPlanRequest,
  AddMonthlyScheduleFormData,
  SearchParameter,
} from './types';
import { confirmDialog } from '@utils/confirmDialog';
import { tableMonthlyActions } from '../monthlyTable';
import { commonActions } from '@ducks/common';
import { CustomPropertySearchFromData } from '@ducks/customProperty/types';

/**
 * @summary 検索パラメータを元に月次計画のリストを取得
 * @param {number | null} propertyId 建物ID
 * @param {number} year 年
 * @param {number} month 月
 * @param {number} sort ソート番号
 * @param {string} keyword 検索キーワード
 * @param {SearchParameter} params 詳細検索条件
 * @param {number[]} tagIds 選択タグIDリスト
 * @param {CustomPropertySearchFromData} customPropertySearchForm カスタムプロパティの検索条件(body)
 * @returns {void}
 */
export const getPlansMonthlyAsync =
  (
    propertyId: number | null,
    year: number,
    month: number,
    sort: number,
    keyword: string,
    params: SearchParameter,
    tagIds: number[],
    customPropertySearchForm?: CustomPropertySearchFromData,
  ) =>
  async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    const reqParams: APIGetMonthlyPlansRequestParams = {
      ...params,
      year: year,
      month: month,
      sort: sort,
    };

    if (propertyId != null) reqParams.property_id = propertyId;
    if (keyword) reqParams.keyword = keyword;
    if (tagIds.length) reqParams.tag = tagIds.join(',');

    await axios
      .post<APIGetMonthlyPlansResponse>(
        '/plans/monthly',
        { ...customPropertySearchForm },
        { params: reqParams },
      )
      .then((response) => {
        const { items, summary, holidays } = response.data.result;
        dispatch(actions.setPlans(items));
        dispatch(actions.setSummary(summary));
        dispatch(actions.setHolidays(holidays));
      })
      .catch(() => {
        dispatch(actions.setPlans([]));
        dispatch(actions.setSummary(null));
        dispatch(actions.setHolidays([]));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary 計画IDを元に月次スケジュールを個別取得
 * @param {number} planId 計画ID
 * @returns {void}
 */
export const getTargetPlansMonthlyAsync = (planId: number) => async (dispatch: any) => {
  dispatch(actions.setIsTargetPlanLoading(true));

  const reqParams: APIGetMonthlyTargetPlansRequestParams = { plan_id: planId };

  await axios
    .get<APIGetMonthlyTargetPlansResponse>('/plans/monthly/plan', {
      params: reqParams,
    })
    .then((response) => {
      dispatch(actions.setTargetPlan(response.data.result));
    })
    .catch(() => {
      dispatch(actions.setTargetPlan(null));
    });

  dispatch(actions.setIsTargetPlanLoading(false));
};

/**
 * @summary 検索パラメータを元に月次計画のリストを出力
 * @param {number | null} propertyId 建物ID
 * @param {number} year 年
 * @param {number} month 月
 * @param {number} sort ソート番号
 * @param {string} keyword 検索キーワード
 * @param {SearchParameter} params 詳細検索条件
 * @param {number[]} tagIds 選択タグIDリスト
 * @param {CustomPropertySearchFromData} customPropertySearchForm カスタムプロパティの検索条件(body)
 * @returns {void}
 */
export const downloadPlanMonthlyScheduleAsync =
  (
    propertyId: number | null,
    year: number,
    month: number,
    sort: number,
    keyword: string,
    params: SearchParameter,
    tagIds: number[],
    customPropertySearchForm?: CustomPropertySearchFromData,
  ) =>
  async (dispatch: any) => {
    dispatch(commonActions.setIsProcessing(true));

    const reqParams: APIGetMonthlyPlansRequestParams = {
      ...params,
      year: year,
      month: month,
      sort: sort,
    };

    if (propertyId != null) reqParams.property_id = propertyId;
    if (keyword) reqParams.keyword = keyword;
    if (tagIds.length) reqParams.tag = tagIds.join(',');

    return await axios
      .post('/plans/monthly/export', { ...customPropertySearchForm }, { params: reqParams })
      .then((response) => {
        return response.data.result;
      })
      .catch((e) => {
        alert(e.message);
        return null;
      })
      .finally(() => {
        dispatch(commonActions.setIsProcessing(false));
      });
  };

/**
 * @summary 新規計画を登録する
 * @param {AddMonthlyScheduleFormData} params 新規作成フォーム入力データ
 * @returns {Promise<boolean>} 登録成否
 */
export const createMonthlyScheduleAsync =
  (params: AddMonthlyScheduleFormData) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .post(`/plans/monthly/table`, {
        ...params,
      })
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 月次計画-予定調整先反映
 * @param {number} planId 計画ID
 * @returns {void}
 */
export const putAdjustmentTargetsAsync = (planId: number) => async (dispatch: any) => {
  dispatch(actions.setIsSubmitting(true));

  await axios
    .put(`/plans/monthly/adjustment/?plan_id=${planId}`)
    .then(() => {
      dispatch(actions.refresh());
      dispatch(tableMonthlyActions.refresh());
    })
    .catch(() => {});

  dispatch(actions.setIsSubmitting(false));
};

/**
 * @summary 個別に計画を編集する
 * @param {APIPutEditPlanRequest} params フォーム入力データ
 * @param {() => void} succeedProcess 保存成功時に実行する処理
 * @param {string} confirmText 建物担当者に登録されていないユーザを建物スタッフとして登録するかの確認文言
 * @param {boolean} isSet 建物担当者に登録されていないユーザを建物スタッフとして登録するか
 * @returns {void}
 */
export const editPlanAsync =
  (params: APIPutEditPlanRequest, succeedProcess: () => void, confirmText = '', isSet = false) =>
  async (dispatch: any) => {
    dispatch(actions.setIsSubmitting(true));

    await axios
      .put(`/plans/monthly${isSet ? `?is_set=${true}` : ''}`, {
        ...params,
      })
      .then(async (response) => {
        if (response.status !== 202) {
          succeedProcess();
          dispatch(actions.refresh());
          return;
        }

        if (!(await confirmDialog(confirmText))) return;
        return dispatch(editPlanAsync(params, succeedProcess, confirmText, true));
      })
      .catch(() => {});

    dispatch(actions.setIsSubmitting(false));
  };

/**
 * @summary 個別に計画を削除する
 * @param {number} planId 削除対象の計画ID
 * @returns {void}
 */
export const deletePlanAsync = (planId: number) => async (dispatch: any) => {
  dispatch(actions.setIsSubmitting(true));

  await axios
    .patch('/plans/monthly/table/delete', {
      plan_list: [{ plan_id: planId }],
    })
    .then(() => {
      dispatch(actions.refresh());
    })
    .catch(() => {});

  dispatch(actions.setIsSubmitting(false));
};
